import { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { UpdateOrganizationMutation } from "src/api/Organization";
import BackButton from "src/components/buttons/BackButton";
import { BorderButton } from "src/components/buttons/BorderButton";
import CancelButton from "src/components/buttons/CancelButton";
import { useForm } from "src/components/forms/Form";
import { InputItem } from "src/components/inputs/InputItem";
import InputRadio from "src/components/inputs/InputRadio";
import { InputRadioGroup } from "src/components/inputs/InputRadioGroup";
import ManageIcon from "src/components/items/ManageIcon";
import Container from "src/components/layout/Container";
import OrganizationModel from "src/models/OrganizationModel";
import { apiClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import { PrivateTerm, getPluralTerminology } from "src/utils/terminology";
import {
  BetaAccessType,
  updateOrganizationVariables,
} from "types/code-generator";

interface Props {}

const steps = {
  "programming-memberships-private": {
    true: "programming-private",
    false: "finish-membership",
  },
  "programming-private": {
    true: "finish-programming",
    false: "terminology-private",
  },
  "memberships-private": {
    true: "terminology-private",
    false: "finish-membership",
  },
  "programming-memberships": {
    true: "finish-programming",
    false: "finish-membership",
  },
  "terminology-private": {
    next: "finish-private",
  },
  "finish-membership": "/admin/membership-offerings/add",
  "finish-programming": "/admin/group-offerings/add",
  "finish-private": "/admin/private-offerings/add",
};

function getType(organization: OrganizationModel) {
  let type:
    | "programming-memberships-private"
    | "programming-private"
    | "memberships-private"
    | "programming-memberships" = "programming-memberships-private";

  if (
    organization.betaAccess.includes(BetaAccessType.Programming) &&
    organization.betaAccess.includes(BetaAccessType.Memberships) &&
    organization.betaAccess.includes(BetaAccessType.PrivateLessons)
  ) {
    type = "programming-memberships-private";
  } else if (
    organization.betaAccess.includes(BetaAccessType.Programming) &&
    organization.betaAccess.includes(BetaAccessType.PrivateLessons)
  ) {
    type = "programming-private";
  } else if (
    organization.betaAccess.includes(BetaAccessType.Memberships) &&
    organization.betaAccess.includes(BetaAccessType.PrivateLessons)
  ) {
    type = "memberships-private";
  } else if (
    organization.betaAccess.includes(BetaAccessType.Programming) &&
    organization.betaAccess.includes(BetaAccessType.Memberships)
  ) {
    type = "programming-memberships";
  }

  return type;
}

export default function FirstOffering(props: Props) {
  const history = useHistory();
  const form = useForm();
  const store = useStore();
  const match = useParams<{ step: string }>();

  const step = match.step;
  const organizationType = getType(store.organization);

  useEffect(() => {
    if (!step) {
      history.replace(`/admin/first-offering/${organizationType}`);
    }
  }, []);

  function cancel() {
    history.push("/admin");
  }

  async function next() {
    const response = form.getFieldValue("response");
    const nextStep: keyof typeof steps = steps[step][response];

    console.log("step", step);
    console.log("nextStep", nextStep);

    // return;

    if (step === "terminology-private") {
      const variables: updateOrganizationVariables = {
        data: {
          terminology: {
            update: {
              private: form.getFieldValue("term"),
            },
          },
        },
        where: {
          id: store.organization.id,
        },
      };

      await Promise.all([
        apiClient(UpdateOrganizationMutation, {
          variables,
        }),
        store.updateOrganization(),
      ]);
    }

    form.resetFields();

    if (nextStep) {
      history.push(`/admin/first-offering/${nextStep}`);
    } else {
      history.push(steps[step]);
    }
  }

  let content = <></>;
  let title = "Create Registration Opportunities";

  if (step === "programming-memberships-private") {
    content = (
      <>
        <hr />
        <InputItem
          form={form}
          name="response"
          label="Will this registration offering have scheduled meeting times?"
        >
          <InputRadioGroup>
            <InputRadio
              value="true"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>Yes</div>
                  <div className="text-sm text-empty">
                    Ex. - classes, clinics, camps, events, teams, training,
                    private lessons, personal training, tutoring, etc.
                  </div>
                </div>
              }
            />
            <InputRadio
              value="false"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>
                    No, I’m setting up a non-schedule registration that grants
                    access to programming and/or facility.
                  </div>
                  <div className="text-sm text-empty">
                    Ex. - memberships, registration fees, guest passes, punch
                    cards, drop-in access, etc.
                  </div>
                </div>
              }
            />
          </InputRadioGroup>
        </InputItem>
        <hr className="mb-8" />
      </>
    );
  } else if (step === "programming-private") {
    content = (
      <>
        <hr />
        <InputItem
          form={form}
          name="response"
          label="Will this registration offering have scheduled meeting times?"
        >
          <InputRadioGroup>
            <InputRadio
              value="true"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>
                    We have a set schedule. Our{" "}
                    {store.organization.terminology.customer.toLowerCase()}{" "}
                    register for pre-set dates, days, and times.
                  </div>
                </div>
              }
            />
            <InputRadio
              value="false"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>
                    We will work with customers and create each schedule based
                    on their input.
                  </div>
                </div>
              }
            />
          </InputRadioGroup>
        </InputItem>
        <hr className="mb-8" />
      </>
    );
  } else if (step === "memberships-private") {
    content = (
      <>
        <hr />
        <InputItem
          form={form}
          name="response"
          label="Will this registration offering have scheduled meeting times?"
        >
          <InputRadioGroup>
            <InputRadio
              value="true"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>Yes</div>
                  <div className="text-sm text-empty">
                    Ex. - customized schedule for private lessons, personal
                    training, tutoring, etc.
                  </div>
                </div>
              }
            />
            <InputRadio
              value="false"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>
                    No, I’m setting up a non-schedule registration that grants
                    access to programming and/or facility.
                  </div>
                  <div className="text-sm text-empty">
                    Ex. - memberships, registration fees, guest passes, punch
                    cards, drop-in access, etc.
                  </div>
                </div>
              }
            />
          </InputRadioGroup>
        </InputItem>
        <hr className="mb-8" />
      </>
    );
  } else if (step === "programming-memberships") {
    content = (
      <>
        <hr />
        <InputItem
          form={form}
          name="response"
          label="Will this registration offering have scheduled meeting times?"
        >
          <InputRadioGroup>
            <InputRadio
              value="true"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>Yes</div>
                  <div className="text-sm text-empty">
                    Ex. - classes, clinics, camps, events, teams, training, etc.
                  </div>
                </div>
              }
            />
            <InputRadio
              value="false"
              className="items-start"
              label={
                <div className="max-w-lg">
                  <div>
                    No, I’m setting up a non-schedule registration that grants
                    access to programming and/or facility.
                  </div>
                  <div className="text-sm text-empty">
                    Ex. - memberships, registration fees, guest passes, punch
                    cards, drop-in access, etc.
                  </div>
                </div>
              }
            />
          </InputRadioGroup>
        </InputItem>
        <hr className="mb-8" />
      </>
    );
  } else if (step === "finish-programming") {
    title = "All set!";
    content = (
      <>
        <p>
          <strong>
            It’s time to create your first offering under Programming.
          </strong>
        </p>
        <p>
          When you’re finished, you can manage this offering and registrations
          from this button:
        </p>

        <div className="mb-8 rounded-md border border-b-3 border-borderGray bg-white px-4 pb-3.5 pt-3">
          <div className="flex items-center">
            <ManageIcon>
              <i aria-hidden className="far fa-sitemap" />
            </ManageIcon>
            <div className="flex-1 overflow-hidden pl-4">
              <p className="mb-0">
                <strong>Programming</strong>
              </p>
              <p className="mb-0 block truncate text-sm text-empty">
                {store.unreadItems.inProgressClasses?.toLocaleString()}{" "}
                in-progress •{" "}
                {store.unreadItems.upcomingClasses?.toLocaleString()} upcoming •{" "}
                {store.unreadItems.enrolledParticipants.toLocaleString()}{" "}
                enrolled
              </p>
            </div>
          </div>
        </div>
      </>
    );
  } else if (step === "finish-membership") {
    title = "All set!";
    content = (
      <>
        <p>
          <strong>
            It’s time to create your first offering under Memberships.
          </strong>
        </p>
        <p>
          When you’re finished, you can manage this offering and registrations
          from this button:
        </p>

        <div className="mb-8 rounded-md border border-b-3 border-borderGray bg-white px-4 pb-3.5 pt-3">
          <div className="flex items-center">
            <ManageIcon>
              <i aria-hidden className="far fa-certificate" />
            </ManageIcon>
            <div className="flex-1 overflow-hidden pl-4">
              <p className="mb-0">
                <strong>Memberships</strong>
              </p>
              <p className="mb-0 block truncate text-sm text-empty">
                {store.unreadItems.inProgressMembershipOfferings.toLocaleString()}{" "}
                active
              </p>
            </div>
          </div>
        </div>
      </>
    );
  } else if (step === "finish-private") {
    title = "All set!";
    const pending =
      store.unreadItems.pendingPrivateSeries +
      store.unreadItems.postedPrivateSeries;

    content = (
      <>
        <p>
          <strong>
            It’s time to create your first offering under{" "}
            {store.organization.terminology.private}.
          </strong>
        </p>
        <p>
          When you’re finished, you can manage this offering and registrations
          from this button:
        </p>

        <div className="mb-8 rounded-md border border-b-3 border-borderGray bg-white px-4 pb-3.5 pt-3">
          <div className="flex items-center">
            <ManageIcon>
              <i aria-hidden className="far fa-clipboard-list-check" />
            </ManageIcon>
            <div className="flex-1 pl-4">
              <p className="mb-0">
                <strong>Custom {store.organization.privateTerminology}</strong>
              </p>
              <p className="mb-0 text-sm text-empty">
                {pending} pending • {store.unreadItems.inProgressPrivateSeries}{" "}
                in-progress
              </p>
            </div>
          </div>
        </div>
      </>
    );
  } else if (step === "terminology-private") {
    title = "Great! Let’s set your terminology.";
    content = (
      <>
        <hr />
        <InputItem
          form={form}
          name="term"
          label={
            <>
              What best describes your offerings that include custom scheduling
              input from{" "}
              {getPluralTerminology(
                store.organization.terminology.customer.toLowerCase()
              )}
              ?
            </>
          }
        >
          <InputRadioGroup
            onChange={(term) => {
              form.setFieldsValue({ response: "next" });
            }}
          >
            {Object.keys(PrivateTerm).map((k) => {
              return (
                <InputRadio
                  key={k}
                  value={k}
                  label={<div className="max-w-lg">{k}</div>}
                />
              );
            })}
          </InputRadioGroup>
        </InputItem>
        <hr className="mb-8" />
      </>
    );
  }

  return (
    <Container full main className="bg-white">
      <Container large className="pb-12 pt-8">
        {step === organizationType ? (
          <CancelButton onClick={cancel} />
        ) : (
          <BackButton />
        )}

        <h1 className="mb-6 mt-2 text-3xl font-bold text-organization">
          {title}
        </h1>

        <div>{content}</div>

        <div className="flex gap-4">
          <BorderButton type="submit" onClick={next}>
            Next
          </BorderButton>
          <BorderButton onClick={cancel}>Cancel</BorderButton>
        </div>
      </Container>
    </Container>
  );
}
